import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  concatNonceAndCiphertext,
  decryptAES,
  encryptAES,
  generateAESKey,
  splitNonceAndCiphertext,
  generateX25519KeyPair,
  importPublicKey,
  wrapKeyForRecipient
} from './crypto';
import { getFeedKey, saveFeedKey } from './storage';

export const FEED_KEY_VERSION = 'v1';

export async function importFeedKey(rawKeyBase64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', base64ToArrayBuffer(rawKeyBase64), { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

export async function getOrCreateFeedKey(version: string = FEED_KEY_VERSION): Promise<CryptoKey> {
  const existing = await getFeedKey(version);
  if (existing) {
    return importFeedKey(existing.keyData);
  }

  const key = await generateAESKey();
  const rawKey = (await crypto.subtle.exportKey('raw', key)) as ArrayBuffer;
  await saveFeedKey({
    version,
    keyData: arrayBufferToBase64(rawKey),
    createdAt: new Date().toISOString()
  });

  return key;
}

export async function encryptPost(content: string, key: CryptoKey): Promise<string> {
  const plaintext = new TextEncoder().encode(content).buffer;
  const { ciphertext, nonce } = await encryptAES(key, plaintext as ArrayBuffer);
  return arrayBufferToBase64(concatNonceAndCiphertext(nonce, ciphertext));
}

export async function decryptPost(ciphertextBase64: string, key: CryptoKey): Promise<string> {
  const payload = base64ToArrayBuffer(ciphertextBase64);
  const { nonce, ciphertext } = splitNonceAndCiphertext(payload);
  const plaintext = await decryptAES(key, ciphertext, nonce);
  return new TextDecoder().decode(plaintext);
}

export async function wrapFeedKeyForUsername(feedKey: CryptoKey, identityPkBase64: string): Promise<{ ciphertext: string; ephemeralPk: string }> {
  const recipientPublicKey = await importPublicKey(base64ToArrayBuffer(identityPkBase64));
  const senderPair = await generateX25519KeyPair();
  const wrapped = await wrapKeyForRecipient(feedKey, recipientPublicKey, senderPair.privateKey);
  return {
    ciphertext: arrayBufferToBase64(wrapped.wrappedKey),
    ephemeralPk: arrayBufferToBase64(wrapped.ephemeralPublicKey)
  };
}
